import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import EmailIcon from "@mui/icons-material/Email";

import CustomMRTTable from "../../components/CustomMRTTable/CustomMRTTable";
import styles from "./LicenseList.module.css";
import { AxiosInstance } from "../../services/Api";
import PopupModal from "../../components/PopupModal/PopupModal";

const getAccountTypeLabel = (accountType) =>
  Number(accountType) === 1 ? "Paid" : "Free";

const LicenseList = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [error, setError] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  const [confirmState, setConfirmState] = useState({
    open: false,
    row: null,
  });

  const fetchLicenses = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await AxiosInstance.get(
        `/licenses?page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}`,
      );

      if (!response || response.status !== 200) {
        throw new Error("Failed to fetch licenses");
      }

      const result = response.data;

      if (result?.success) {
        setLicenses(result?.data?.licenses || []);
        setTotalRecords(result?.data?.pagination?.total || 0);
      } else {
        setLicenses([]);
        setTotalRecords(0);
        throw new Error(result?.message || "Failed to fetch licenses");
      }
    } catch (err) {
      console.error("Error fetching licenses:", err);
      setError(
        err?.response?.data?.message || err?.message || "Something went wrong",
      );
      setLicenses([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  const handleResendActivation = useCallback((row) => {
    if (!row?.id) return;
    setConfirmState({ open: true, row });
  }, []);

  const confirmResendActivation = useCallback(async () => {
    const row = confirmState.row;
    if (!row?.id) return;

    setConfirmState({ open: false, row: null });
    setResendingEmail(row.id);

    try {
      const response = await AxiosInstance.post(
        `/licenses/${row.id}/resend-activation`,
      );

      if (response?.status === 200 && response?.data?.success) {
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message || "Activation email sent successfully!",
          redirectPath: "",
        });
      } else {
        throw new Error(
          response?.data?.message || "Failed to send activation email",
        );
      }
    } catch (err) {
      console.error("Error resending activation:", err);
      setModalState({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to send activation email",
        redirectPath: "",
      });
    } finally {
      setResendingEmail(null);
    }
  }, [confirmState.row]);

  const handleEditLicense = useCallback(
    (row) => {
      if (row?.eid) {
        navigate(`/dashboard/licenseCreate?id=${row.eid}`);
      }
    },
    [navigate],
  );

  const handleExcelDownload = useCallback(async () => {
    try {
      if (!licenses.length) return;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "License List";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet("Licenses");
      worksheet.columns = [
        { header: "S.No", key: "sno", width: 8 },
        { header: "Client ID", key: "client_id", width: 14 },
        { header: "Name", key: "name", width: 22 },
        { header: "School Name", key: "school_name", width: 28 },
        { header: "Email", key: "email", width: 32 },
        { header: "Mobile", key: "client_mobile", width: 16 },
        { header: "User Role", key: "role_display_name", width: 18 },
        { header: "Account Type", key: "account_type", width: 14 },
        { header: "Is Validated", key: "is_validated", width: 14 },
      ];

      const headerRow = worksheet.getRow(1);
      headerRow.height = 24;
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, size: 11 };
        cell.alignment = {
          vertical: "middle",
          horizontal: "center",
          wrapText: true,
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE0E0E0" },
        };
        cell.border = {
          top: { style: "thin", color: { argb: "FF999999" } },
          left: { style: "thin", color: { argb: "FF999999" } },
          bottom: { style: "thin", color: { argb: "FF999999" } },
          right: { style: "thin", color: { argb: "FF999999" } },
        };
      });

      licenses.forEach((item, index) => {
        worksheet.addRow({
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          client_id: item.client_id ?? "",
          name: item.name || "",
          school_name: item.school_name || "",
          email: item.email || "",
          client_mobile: item.client_mobile || "",
          role_display_name: item.role_display_name || "",
          account_type: getAccountTypeLabel(item.account_type),
          is_validated: Number(item.is_validated) === 1 ? "Yes" : "No",
        });
      });

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFCCCCCC" } },
            left: { style: "thin", color: { argb: "FFCCCCCC" } },
            bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
            right: { style: "thin", color: { argb: "FFCCCCCC" } },
          };
          cell.alignment = {
            vertical: "middle",
            horizontal: colNumber === 1 ? "center" : "left",
            wrapText: true,
          };
          cell.font = { size: 10 };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `LICENSE_LIST_Page_${pagination.pageIndex + 1}.xlsx`);
    } catch (err) {
      console.error("Error generating Excel:", err);
    }
  }, [licenses, pagination.pageIndex, pagination.pageSize]);

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "S.No",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "client_id",
        header: "Client ID",
        size: 100,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue() ?? "—",
      },
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "school_name",
        header: "School Name",
        size: 180,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 220,
        enableSorting: true,
        enableColumnFilter: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "client_mobile",
        header: "Mobile",
        size: 130,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "role_display_name",
        header: "User Role",
        size: 140,
        enableSorting: true,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "account_type",
        header: "Account Type",
        size: 130,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: ["Paid", "Free"],
        Cell: ({ cell }) => {
          const isPaid = Number(cell.getValue()) === 1;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                background: isPaid ? "#E3F2FD" : "#FFF3E0",
                color: isPaid ? "#1565C0" : "#E65100",
                border: `1px solid ${isPaid ? "#90CAF9" : "#FFCC80"}`,
              }}
            >
              {getAccountTypeLabel(cell.getValue())}
            </span>
          );
        },
      },
      {
        accessorKey: "is_validated",
        header: "Is Validated",
        size: 130,
        enableSorting: true,
        filterVariant: "select",
        filterSelectOptions: ["Yes", "No"],
        Cell: ({ cell }) => {
          const isValidated = Number(cell.getValue()) === 1;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 600,
                background: isValidated ? "#E8F5E9" : "#FFEBEE",
                color: isValidated ? "#2E7D32" : "#C62828",
                border: `1px solid ${isValidated ? "#A5D6A7" : "#EF9A9A"}`,
              }}
            >
              {isValidated ? "Yes" : "No"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 110,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        Cell: ({ row }) => {
          const isSending = resendingEmail === row.original.id;

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Tooltip title="Edit license" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditLicense(row.original);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Resend activation email" arrow>
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    disabled={isSending}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResendActivation(row.original);
                    }}
                  >
                    {isSending ? (
                      <i className="fa-solid fa-spinner fa-spin" />
                    ) : (
                      <EmailIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [
      pagination.pageIndex,
      pagination.pageSize,
      resendingEmail,
      handleEditLicense,
      handleResendActivation,
    ],
  );

  const handleModalOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleModalOk}
      />

      <PopupModal
        open={confirmState.open}
        title="Confirm"
        render
        onClose={() => setConfirmState({ open: false, row: null })}
        onOk={confirmResendActivation}
        renderData={() => (
          <div>
            <p
              style={{
                margin: "0 0 20px",
                textAlign: "center",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              Are you sure you want to resend activation email to{" "}
              <strong>
                {confirmState.row?.email || confirmState.row?.name}
              </strong>
              ?
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() => setConfirmState({ open: false, row: null })}
                style={{
                  padding: "8px 20px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmResendActivation}
                style={{
                  padding: "8px 20px",
                  borderRadius: "6px",
                  border: "none",
                  background: "#1565C0",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Resend
              </button>
            </div>
          </div>
        )}
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>LICENSE LIST</h1>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.createButton}
              onClick={() => navigate("/dashboard/licenseCreate")}
              aria-label="Create License"
              title="Create License"
            >
              <i className="fa-solid fa-plus" aria-hidden />
              <span>Create License</span>
            </button>

            <Tooltip title="Refresh list" arrow>
              <span>
                <button
                  type="button"
                  onClick={fetchLicenses}
                  className={styles.iconButton}
                  aria-label="Refresh"
                  disabled={loading}
                >
                  <i
                    className={`fa-solid fa-rotate ${styles.refreshIcon}`}
                    aria-hidden
                  />
                </button>
              </span>
            </Tooltip>

            <Tooltip title="Download Excel" arrow>
              <span>
                <button
                  type="button"
                  onClick={handleExcelDownload}
                  className={styles.iconButton}
                  aria-label="Download Excel"
                  disabled={loading || licenses.length === 0}
                >
                  <i
                    className={`fa-solid fa-download ${styles.downloadIcon}`}
                    aria-hidden
                  />
                </button>
              </span>
            </Tooltip>
          </div>
        </div>

        {error ? (
          <div className={styles.errorMessage}>
            {error}
            <button
              type="button"
              onClick={fetchLicenses}
              className={styles.retryButton}
            >
              Retry
            </button>
          </div>
        ) : null}

        <div className={styles.tableSection}>
          <CustomMRTTable
            columns={columns}
            data={licenses}
            loading={loading}
            manualPagination
            manualFiltering={false}
            rowCount={totalRecords}
            pagination={pagination}
            onPaginationChange={setPagination}
            enablePagination
            enableSorting
            enableColumnFilters
            enableSearch
            enableColumnVisibility
            enableDensityToggle
            enableFullScreenToggle
            enableHiding
            enableStickyHeader
            placeholder="Search licenses on this page..."
          />
        </div>
      </div>
    </>
  );
};

export default LicenseList;
